import { DashboardLayout } from "../../components/dashboard-layout";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../theme";
import axios from "axios";
import { t } from "i18next";
import { 
    Box, 
    Container, 
    Table, 
    TableBody, 
    TableCell, 
    TableHead, 
    TableRow, 
    TextField, 
    Typography, 
    Card, 
    Pagination,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button
} from "@mui/material";
import { useEffect, useState } from "react";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";

function AuditLogs() {
    const [logs, setLogs] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [actor, setActor] = useState("");
    const [action, setAction] = useState("");
    const [status, setStatus] = useState("");
    const [load, setLoad] = useState(false);

    axios.interceptors.request.use(
        (config) => {
            const token = localStorage.getItem("user");
            if (token) {
                config.headers["Authorization"] = `Bearer ${token}`;
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    const fetchLogs = () => {
        const params = {
            page: page,
            limit: 20
        };
        if (actor) params.actor = actor;
        if (action) params.action = action;
        if (status) params.status = status;

        axios
            .get(`${process.env.REACT_APP_API_URL}/admin-sec/audit-events`, { params })
            .then((res) => {
                if (res.data.success) {
                    setLogs(res.data.data);
                    setTotalPages(res.data.totalPages || 1);
                }
            })
            .catch((err) => {
                console.error(err);
            });
    };

    useEffect(() => {
        fetchLogs();
    }, [page, load]);

    const handleFilter = (e) => {
        e.preventDefault();
        setPage(1);
        setLoad(!load);
    };

    function formatDate(dateStr) {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        const day = ("0" + d.getDate()).slice(-2);
        const month = ("0" + (d.getMonth() + 1)).slice(-2);
        const year = d.getFullYear();
        const hours = ("0" + d.getHours()).slice(-2);
        const minutes = ("0" + d.getMinutes()).slice(-2);
        const seconds = ("0" + d.getSeconds()).slice(-2);
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    }

    function renderDetail(item) {
        if (item.detail && typeof item.detail === "string" && item.detail.trim().length > 0) {
            return item.detail;
        }
        if (!item.metadata) return "";
        try {
            const m = typeof item.metadata === "string" ? JSON.parse(item.metadata) : item.metadata;
            if (item.action === "set_current_baccarat_result" || item.action === "preset_future_baccarat_result") {
                const winnerLabel = m.winner === "P" ? "Player" : m.winner === "B" ? "Banker" : m.winner === "T" || m.winner === "Tie" ? "Hòa" : (m.winner || "");
                const prefix = m.isFuture ? "Cài trước" : "Chỉnh";
                const sanhMatch = (item.target || "").match(/sanh_(\d+)/);
                const sanhLetter = sanhMatch ? String.fromCharCode(64 + Number(sanhMatch[1])) : "A";
                return `${prefix} kết quả ${winnerLabel}${m.roundNo ? ` cho phiên #${m.roundNo}` : ""}, bàn ${sanhLetter}`;
            }
            if (item.action === "set_current_xocdia_blockchain_result" || item.action === "preset_future_xocdia_blockchain_result") {
                const prefix = m.isFuture ? "Cài trước" : "Chỉnh";
                const sanhMatch = (item.target || "").match(/sanh_(\d+)/);
                const sanhLetter = sanhMatch ? String.fromCharCode(64 + Number(sanhMatch[1])) : "A";
                return `${prefix} kết quả ${m.result || ""} cho phiên #${m.roundNo || ""}, bàn ${sanhLetter}`;
            }
            return JSON.stringify(item.metadata);
        } catch (e) {
            return String(item.metadata);
        }
    }

    return (
        <ThemeProvider theme={theme}>
            <DashboardLayout>
                <Box component="main" sx={{ flexGrow: 1, py: 3 }}>
                    <Container maxWidth={false}>
                        <Typography className="source-page-title" variant="h4" sx={{ fontWeight: "bold", mb: 3 }}>
                            <SecurityRoundedIcon sx={{ mr: 1, verticalAlign: "middle", color: "#2563eb" }} /> Nhật Ký Hoạt Động (Audit Logs)
                        </Typography>

                        {/* Filter Form */}
                        <Card sx={{ p: 3, mb: 4, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                            <TextField
                                label="Admin/Nhân viên"
                                size="small"
                                value={actor}
                                onChange={(e) => setActor(e.target.value)}
                                sx={{ minWidth: 200 }}
                            />
                            <TextField
                                label="Hành động"
                                size="small"
                                value={action}
                                onChange={(e) => setAction(e.target.value)}
                                sx={{ minWidth: 200 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Trạng thái</InputLabel>
                                <Select
                                    value={status}
                                    label="Trạng thái"
                                    onChange={(e) => setStatus(e.target.value)}
                                >
                                    <MenuItem value="">Tất cả</MenuItem>
                                    <MenuItem value="success">Thành công (success)</MenuItem>
                                    <MenuItem value="failed">Thất bại (failed)</MenuItem>
                                </Select>
                            </FormControl>
                            <Button variant="contained" color="primary" onClick={handleFilter}>
                                Lọc
                            </Button>
                        </Card>

                        {/* Logs Table */}
                        <Card sx={{ overflowX: "auto" }}>
                            <Table>
                                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: "bold" }}>Thời gian</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Tài khoản</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Vai trò</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Hành động</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Đối tượng (Entity)</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Target ID</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Trạng thái</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }}>Chi tiết (Detail)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logs.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                                Chưa có ghi chép nhật ký nào
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        logs.map((item) => (
                                            <TableRow key={item._id} hover>
                                                <TableCell>{formatDate(item.createdAt)}</TableCell>
                                                <TableCell sx={{ fontWeight: "bold" }}>{item.actor_username}</TableCell>
                                                <TableCell>{item.actor_role}</TableCell>
                                                <TableCell sx={{ color: "primary.main", fontWeight: "600" }}>{item.action}</TableCell>
                                                <TableCell>{item.entity}</TableCell>
                                                <TableCell>{item.target}</TableCell>
                                                <TableCell sx={{ 
                                                    color: item.status === "success" ? "success.main" : "error.main",
                                                    fontWeight: "bold" 
                                                }}>
                                                    {item.status}
                                                </TableCell>
                                                <TableCell sx={{ maxWidth: 350, wordBreak: "break-word" }}>
                                                    {renderDetail(item)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </Card>

                        {/* Pagination */}
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, p) => setPage(p)}
                                color="primary"
                            />
                        </Box>
                    </Container>
                </Box>
            </DashboardLayout>
        </ThemeProvider>
    );
}

export default AuditLogs;
